import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useQuery, useApolloClient } from '@apollo/client';
import { CURRENT_USER } from '../App.gql';
import { useIdentity } from '../Authentication/Authentication.hooks';

import { PRODUCT_VARIANT_COUNT } from './Navigation.gql';
import { refreshSession } from '../Authentication/Authentication.utils';
import { useAuthState } from '../Authentication/Authentication.context';
import NavDrawer from './components/NavDrawer/NavDrawer';
import NavFlyout from './components/NavFlyout/NavFlyout';
import { flattenSections } from './components/NavDrawer/NavDrawer.utils';
import { BUILD } from '../../util/const';
import { vercel_base_url } from '../../util/config';

import NotificationIcon from '../Icons/NotificationIcon/NotificationIcon';
import * as Styled from './Navigation.styled';

/**
 * This component displays the top level navigation for the site.
 */
const Navigation = ({ sections, withSearch, onRequestOpen }) => {
  const { setIsDialogOpen } = useAuthState();
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const flyoutCloseTimer = useRef(null);
  const FLYOUT_CLOSE_DELAY = 300; // milliseconds
  const router = useRouter();

  /** Testing Purposes for NavFlyout
   * Sets NavFlyout to always be open after hovering a NavLink
   * Can still be closed via Escape key upon tabbing into it */
  const urlParams = isMounted && new URLSearchParams(window.location.search);
  const alwaysOpened = urlParams && urlParams.get('open') === 'true';

  const client = useApolloClient();

  const { user } = useIdentity();
  const { data } = useQuery(CURRENT_USER);
  const cartId = data?.cartId;

  const { data: cartData } = useQuery(PRODUCT_VARIANT_COUNT, {
    context: { service: 'storefront' },
    variables: { cartId },
    skip: !cartId
  });

  const productVariantCount = cartData?.cart?.lines.productVariantCount || 0;

  const handleButtonClick = (e) => {
    e.preventDefault();
    const path = e.currentTarget.getAttribute('href');
    if (path) router.push(path);
  };

  const cancelCloseFlyout = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const openFlyout = useCallback((section) => {
    cancelCloseFlyout();
    setIsFlyoutOpen(true);
    setCurrentSection(section);
  }, []);

  const closeFlyout = useCallback(() => {
    setIsFlyoutOpen(false);
    setCurrentSection(null);
  }, []);

  const scheduleCloseFlyout = () => {
    cancelCloseFlyout();
    flyoutCloseTimer.current = setTimeout(() => {
      closeFlyout();
    }, FLYOUT_CLOSE_DELAY);
  };

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      cancelCloseFlyout();
    };
  }, []);

  const slidingPanels = flattenSections(sections);

  return (
    <Styled.Navigation>
      <Styled.NavigationBar>
        <Styled.LogoContainer>
          <Styled.LogoLink
            onMouseEnter={() => {
              if (!alwaysOpened && isFlyoutOpen) {
                closeFlyout();
              }
            }}
            href={BUILD === 'shopify' ? vercel_base_url : '/'}
            aria-label="Bombas"
            as={BUILD === 'shopify' ? 'a' : undefined}
          >
            <Styled.Icon name="logo-type" size={20} />
          </Styled.LogoLink>
        </Styled.LogoContainer>

        <Styled.LeftIcons>
          <Styled.ButtonIcon
            name="burger"
            iconSize={24}
            onClick={() => {
              setIsNavDrawerOpen(true);
            }}
            data-track-label="Hamburger"
            data-track-property="open"
            aria-label="Site Navigation"
          />

          {withSearch && (
            <Styled.LeftSearchIcon
              name="search"
              iconSize={22}
              onClick={onRequestOpen}
              data-track-label="Search"
              data-track-property="open"
              data-testid="headerSearch"
              aria-label="Search"
            />
          )}
        </Styled.LeftIcons>

        <Styled.DesktopLinkContainer
          onMouseEnter={cancelCloseFlyout}
          onMouseLeave={(e) => {
            const relatedTarget = e.relatedTarget || document.activeElement;
            if (
              relatedTarget?.closest &&
              relatedTarget.closest('.flyout-container')
            ) {
              return;
            }
            scheduleCloseFlyout();
          }}
        >
          {sections.map((section, idx) => (
            <Styled.NavLink
              key={idx}
              href={section.url}
              onClick={handleButtonClick}
              onMouseEnter={() => openFlyout(section)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  openFlyout(section);
                }
                if (e.key === 'Escape') {
                  closeFlyout();
                }
              }}
              active={section === currentSection}
            >
              {section.text}
            </Styled.NavLink>
          ))}
        </Styled.DesktopLinkContainer>

        <Styled.RightIcons>
          <Styled.RightInnerIcons
            onMouseEnter={() => {
              if (!alwaysOpened && isFlyoutOpen) {
                closeFlyout();
              }
            }}
          >
            {isMounted && user.isLoggedIn && (
              <>
                <Styled.BuyAgainLink
                  as={BUILD === 'shopify' ? 'a' : Link}
                  href={
                    BUILD === 'shopify'
                      ? `${vercel_base_url}/buy-again`
                      : '/buy-again'
                  }
                >
                  Buy Again
                </Styled.BuyAgainLink>
                <Styled.ButtonIcon
                  name="account"
                  iconSize={24}
                  onClick={async (e) => {
                    e.preventDefault();
                    refreshSession(client);
                    PubSub.publish('account_nav.open');
                    PubSub.publish('cart.close');
                  }}
                  data-track-label="My Account"
                  aria-label="My Account"
                />
              </>
            )}
            {isMounted && !user.isLoggedIn && (
              <Styled.LogInButton
                variant="secondary"
                isGhost
                onClick={() => setIsDialogOpen(true)}
                data-track-label="Log In"
                aria-label="Log In"
              >
                Log In
              </Styled.LogInButton>
            )}
            {withSearch && (
              <Styled.RightSearchIcon
                name="search"
                iconSize={22}
                onClick={onRequestOpen}
                data-track-label="Search"
                data-track-property="open"
                data-testid="headerSearch"
                aria-label="Search"
              />
            )}
            <NotificationIcon
              name="bag"
              count={productVariantCount}
              onClick={() => {
                PubSub.publish('cart.open');
                PubSub.publish('account_nav.close');
              }}
              data-track-label="Cart"
              data-track-property="open"
              aria-label="Cart"
              data-label="miniCart_open"
            />
          </Styled.RightInnerIcons>
        </Styled.RightIcons>
      </Styled.NavigationBar>

      {isMounted && (
        <NavFlyout
          isOpen={isFlyoutOpen}
          level1Section={currentSection}
          onMouseEnter={cancelCloseFlyout}
          onMouseLeave={scheduleCloseFlyout}
          onBackdropEnter={() => {
            if (!alwaysOpened) {
              closeFlyout();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeFlyout();
            }
          }}
          className="flyout-container"
        />
      )}

      {isMounted && (
        <NavDrawer
          isOpen={isNavDrawerOpen}
          isLoggedIn={user.isLoggedIn}
          onRequestClose={() => setIsNavDrawerOpen(false)}
          rootPanel={sections}
          slidingPanels={slidingPanels}
        />
      )}
    </Styled.Navigation>
  );
};

Navigation.propTypes = {
  /**
   * Array of Navigation Sections
   */
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Hides the search button if false
   */
  withSearch: PropTypes.bool,
  /**
   * Opens Search and hides Navigation
   */
  onRequestOpen: PropTypes.func
};

Navigation.defaultProps = {
  sections: [],
  withSearch: true,
  onRequestOpen: undefined
};

export default Navigation;
